import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency, num, dateShort } from "@/lib/format";
import { SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Waste", "Spoilage", "Count Audit", "QA Sampling", "Transfer", "Correction"];

export default function Adjustments() {
  const [items, setItems] = useState([]);
  const [skus, setSkus] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sku_code: "", facility: "", type: "", delta_qty: "", reason: "", auditor: "" });

  const load = () => api.get("/adjustments").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/skus").then((r) => setSkus(r.data));
    api.get("/facilities").then((r) => setFacilities(r.data));
  }, []);

  const submit = async () => {
    if (!form.sku_code || !form.facility || !form.type || form.delta_qty === "") { toast.error("Please fill in all required fields"); return; }
    await api.post("/adjustments", { ...form, delta_qty: Number(form.delta_qty), auditor: form.auditor || "System" });
    toast.success("Adjustment recorded & stock updated");
    setOpen(false);
    setForm({ sku_code: "", facility: "", type: "", delta_qty: "", reason: "", auditor: "" });
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Inventory Adjustments" subtitle="Waste, spoilage, cycle counts and QA sampling ledger"
        action={<Button data-testid="btn-new-adjustment" onClick={() => setOpen(true)} className="bg-stone-900 hover:bg-stone-800 active:scale-[0.98]"><Plus className="h-4 w-4 mr-1.5" />New Adjustment</Button>} />

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">Adj ID</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Item</th>
              <th className="px-4 py-3 text-left">Location</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-right">Delta</th>
              <th className="px-4 py-3 text-left">Reason</th>
              <th className="px-4 py-3 text-right">Value Impact</th>
              <th className="px-4 py-3 text-left">Auditor</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.adj_no} data-testid={`adj-row-${a.adj_no}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 transition-colors">
                <td className="px-4 py-3"><SkuTag code={a.adj_no} /></td>
                <td className="px-4 py-3 text-stone-600">{dateShort(a.date)}</td>
                <td className="px-4 py-3 font-medium text-stone-800">{a.sku_name}</td>
                <td className="px-4 py-3 text-stone-600">{a.facility_name}</td>
                <td className="px-4 py-3 text-stone-600">{a.type}</td>
                <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${a.delta_qty >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{a.delta_qty >= 0 ? "+" : ""}{num(a.delta_qty)}</td>
                <td className="px-4 py-3 text-stone-500 text-xs max-w-[160px] truncate">{a.reason}</td>
                <td className={`px-4 py-3 text-right font-mono tabular-nums ${a.value_impact >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{currency(a.value_impact)}</td>
                <td className="px-4 py-3 text-stone-500 text-xs">{a.auditor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-amber-600" />Record Stock Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-stone-500">SKU / Item</Label>
              <Select value={form.sku_code} onValueChange={(v) => setForm({ ...form, sku_code: v })}>
                <SelectTrigger data-testid="adj-sku-select" className="mt-1"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent className="max-h-64">{skus.map((s) => <SelectItem key={s.sku_code} value={s.sku_code}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-500">Facility</Label>
                <Select value={form.facility} onValueChange={(v) => setForm({ ...form, facility: v })}>
                  <SelectTrigger data-testid="adj-facility-select" className="mt-1"><SelectValue placeholder="Location" /></SelectTrigger>
                  <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-stone-500">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="adj-type-select" className="mt-1"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-stone-500">Delta Qty (+/-)</Label><Input data-testid="adj-delta-input" type="number" value={form.delta_qty} onChange={(e) => setForm({ ...form, delta_qty: e.target.value })} placeholder="-12" className="mt-1" /></div>
              <div><Label className="text-xs text-stone-500">Auditor</Label><Input data-testid="adj-auditor-input" value={form.auditor} onChange={(e) => setForm({ ...form, auditor: e.target.value })} placeholder="Name" className="mt-1" /></div>
            </div>
            <div><Label className="text-xs text-stone-500">Reason</Label><Textarea data-testid="adj-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason code / notes" className="mt-1" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="adj-submit-btn" onClick={submit} className="bg-stone-900 hover:bg-stone-800">Record Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
